
import type {
  StripeAddressElement,
  StripeCheckout,
  StripePaymentElement,
  StripeTaxIdElement
} from '@stripe/stripe-js'
import { defineComponent, PropType } from 'vue'

export default defineComponent({
  name: 'V2CheckoutPaymentCard',
  props: {
    checkout: {
      type: Object as PropType<StripeCheckout | null>,
      default: null
    },
    checkoutActions: {
      type: Object as PropType<any>,
      default: null
    },
    loading: {
      type: Boolean,
      default: false
    },
    error: {
      type: String as PropType<string | null>,
      default: null
    },
    returnUrl: {
      type: String,
      default: ''
    },
    paymentConfirmed: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      // Payment state
      paymentElement: null as StripePaymentElement | null,
      paymentReady: false,
      paymentComplete: false,
      paymentProcessing: false,
      paymentError: null as string | null,
      paymentState: 'idle' as 'idle' | 'processing' | 'success' | 'error',

      // Billing details state
      showBillingDetails: false,
      addressElement: null as StripeAddressElement | null,
      addressReady: false,
      taxIdElement: null as StripeTaxIdElement | null,
      taxIdReady: false,
      billingError: null as string | null
    }
  },
  computed: {
    canSubmit(): boolean {
      return this.paymentReady && this.paymentComplete && !this.paymentProcessing
    }
  },
  watch: {
    paymentConfirmed(val: boolean) {
      if (val) this.paymentState = 'success'
    },
    checkout: {
      immediate: true,
      handler(newCheckout: StripeCheckout | null) {
        if (newCheckout) {
          this.$nextTick(() => {
            this.initializePaymentElement()
          })
        }
      }
    },
    showBillingDetails(newVal: boolean) {
      if (newVal) {
        this.$nextTick(() => {
          this.mountBillingElements()
        })
      } else {
        this.destroyBillingElements()
      }
    }
  },
  beforeDestroy() {
    if (this.paymentElement) {
      this.paymentElement.destroy()
      this.paymentElement = null
    }
    this.destroyBillingElements()
  },
  methods: {
    // --- Payment ---
    initializePaymentElement(): void {
      if (!this.checkout) return

      if (this.paymentElement) {
        this.paymentElement.destroy()
        this.paymentElement = null
      }

      this.paymentReady = false
      this.paymentComplete = false
      this.paymentError = null

      this.paymentElement = this.checkout.createPaymentElement({
        layout: 'tabs'
      })
      this.paymentElement.mount('#v2-payment-element')

      this.paymentElement.on('ready', () => {
        this.paymentReady = true
      })

      this.paymentElement.on('change', (event) => {
        this.paymentComplete = event.complete
      })
    },

    async handlePayment(): Promise<void> {
      if (!this.checkoutActions) return

      this.paymentProcessing = true
      this.paymentState = 'processing'
      this.paymentError = null

      try {
        const result = await this.checkoutActions.confirm({
          returnUrl: this.returnUrl || `${window.location.origin}/`,
          redirect: 'if_required'
        })

        if (result.type === 'error') {
          this.paymentError = result.error.message ?? 'Payment failed. Please try again.'
          this.paymentState = 'error'
          this.$emit('payment-error', this.paymentError)
        } else {
          this.$emit('payment-success')
        }
      } catch (err) {
        this.paymentState = 'error'
        this.paymentError = (err as Error).message ?? 'An unexpected error occurred.'
        this.$emit('payment-error', this.paymentError)
      } finally {
        this.paymentProcessing = false
      }
    },

    // --- Billing details ---
    toggleBillingDetails(): void {
      if (!this.checkout) return
      this.showBillingDetails = !this.showBillingDetails
    },

    retryBillingMount(): void {
      this.destroyBillingElements()
      this.$nextTick(() => {
        this.mountBillingElements()
      })
    },

    mountBillingElements(): void {
      if (!this.checkout) return

      this.billingError = null

      try {
        this.addressElement = this.checkout.createBillingAddressElement({
          display: { name: 'organization' }
        })
        this.addressElement.mount('#v2-billing-address-element')
        this.addressElement.on('ready', () => {
          this.addressReady = true
        })
      } catch {
        this.billingError = 'Failed to load billing details form. Please try again.'
        return
      }

      try {
        this.taxIdElement = this.checkout.createTaxIdElement()
        this.taxIdElement.mount('#v2-tax-id-element')
        this.taxIdElement.on('ready', () => {
          this.taxIdReady = true
        })
      } catch {
        // Tax ID element is non-critical; address collection still works independently.
        this.taxIdReady = true
      }
    },

    destroyBillingElements(): void {
      if (this.addressElement) {
        this.addressElement.destroy()
        this.addressElement = null
      }
      if (this.taxIdElement) {
        this.taxIdElement.destroy()
        this.taxIdElement = null
      }
      this.addressReady = false
      this.taxIdReady = false
      this.billingError = null
    }
  }
})
